import React from 'react';
import { Box, Typography, Separator, formatPrice } from '@lucid/lego';
import type { CheckoutPricing } from '../types';
import styles from './SummaryPanel.module.scss';

type SummaryPanelLabels = {
  quantityTotal?: string;
  subtotalAllConfigurations?: string;
  totalOwedAllConfigurations?: string;
  availableCreditLine?: string;
  remainingAfterPurchase?: string;
};

export type SummaryPanelProps = {
  labels?: SummaryPanelLabels;
  pricing?: CheckoutPricing;
  locale?: string;
  totalVehicles: number;
};

const formatDisplayPrice = (amount: number, locale: string): string =>
  formatPrice(amount, { locale });

/**
 * Financial summary panel showing pricing breakdown and credit status.
 * @component
 * @param {SummaryPanelProps} props
 * @returns {JSX.Element}
 * @example
 * <SummaryPanel pricing={pricing} currencyCode="USD" totalVehicles={3} />
 */
const SummaryPanel: React.FC<SummaryPanelProps> = ({ labels, pricing, locale, totalVehicles }) => (
  <Box className={styles.summaryPanel} data-testid="checkout-summary-panel">
    <Box className={styles.summaryStats}>
      <Box className={styles.summaryStatRow}>
        <Typography variant="body" size="medium" color="white" theme="dark">
          {labels?.quantityTotal ?? 'Quantity Total'}
        </Typography>
        <Typography variant="body" size="medium" color="white" theme="dark">
          {totalVehicles}
        </Typography>
      </Box>
      <Box className={styles.summaryStatRow}>
        <Typography variant="body" size="medium" color="white" theme="dark">
          {labels?.subtotalAllConfigurations ?? 'Subtotal All Configurations'}
        </Typography>
        <Typography variant="body" size="medium" color="white" theme="dark">
          {formatDisplayPrice(pricing?.subtotal ?? 0, locale ?? 'de-de')}
        </Typography>
      </Box>

      <Separator showLine />

      <Box className={styles.summaryTotalRow}>
        <Typography variant="subtitle" size="medium" color="white" theme="dark">
          {labels?.totalOwedAllConfigurations ?? 'Total Owed for All Configurations'}
        </Typography>
        <Typography variant="subtitle" size="medium" color="white" theme="dark">
          {formatDisplayPrice(pricing?.grandTotal ?? 0, locale ?? 'de-de')}
        </Typography>
      </Box>

      <Separator showLine />

      <Box className={styles.summaryStatRow}>
        <Typography variant="body" size="small" color="white" theme="dark">
          {labels?.availableCreditLine ?? 'Available Credit Line'}
        </Typography>
        <Typography variant="body" size="small" color="white" theme="dark">
          {formatDisplayPrice(pricing?.availableCreditLineTotal ?? 0, locale ?? 'de-de')}
        </Typography>
      </Box>
      <Box className={styles.summaryStatRow}>
        <Typography variant="body" size="small" color="white" theme="dark">
          {labels?.remainingAfterPurchase ?? 'Remaining after Purchase'}
        </Typography>
        <Typography variant="body" size="small" color="white" theme="dark">
          {formatDisplayPrice(pricing?.remainingAfterPurchase ?? 0, locale ?? 'de-de')}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default SummaryPanel;
